import React, { useState, useContext, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { NotificationsTwoTone, KeyboardArrowDownTwoTone, SearchTwoTone } from 'common/icons';
import { appContext } from 'common/contexts';
import { store } from 'common/contexts';
import styles from './notifications.module.css';
import { Order } from 'common/types';

// Clave para almacenar las órdenes vistas en el día actual
const VIEWED_ORDERS_KEY = 'viewed_orders_today';

type NotificationsProps = {
  onOrderViewed: (orderId: string) => void;
  onSelectOrder?: (orderId: string) => void;
};

const Notifications: React.FC<NotificationsProps> = ({ onOrderViewed, onSelectOrder }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [expandedOrders, setExpandedOrders] = useState<string[]>([]);
  const [context] = useContext(appContext);
  const { t } = useTranslation();
  const { orders: ordersContext, items } = context;
  
  // Estados locales para manejar las notificaciones sin parpadeos
  const [externalOrders, setExternalOrders] = useState<Order[]>([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [localViewedOrders, setLocalViewedOrders] = useState<string[]>([]);
  
  // Estado para el buscador
  const [searchTerm, setSearchTerm] = useState('');
  
  // Función para verificar si una fecha es del día actual
  const isToday = useCallback((timestamp: number) => {
    const date = new Date(timestamp);
    const today = new Date();
    return date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();
  }, []);
  // Función para obtener un producto por su ID (solo para mostrar su nombre)
  const getItemName = (id: string) => {
    const item = items.find(item => item.id === id);
    return item ? item.name : id;
  };
  // Cargar órdenes vistas al iniciar
  useEffect(() => {
    const loadViewedOrders = async () => {
      try {
        const viewedOrders = await store.getItem<string[]>(VIEWED_ORDERS_KEY) || [];
        setLocalViewedOrders(viewedOrders);
      } catch (error) {
        console.error('Error al cargar órdenes vistas:', error);
      }
    };
    
    loadViewedOrders();
  }, []);
  
  // Actualizar las órdenes externas y el contador cuando cambia el contexto
  useEffect(() => {
    // Filtrar órdenes externas del día actual
    const todayExternalOrders = ordersContext.filter(order => 
      order.isExternal && isToday(order.dateStart)
    );
    
    // Establecer las órdenes externas (todas las del día)
    setExternalOrders(todayExternalOrders);
    
    // Contar solo las que no están marcadas como vistas (ni en el contexto ni localmente)
    const unviewedCount = todayExternalOrders.filter(order => 
      !order.isView && !localViewedOrders.includes(order.id)
    ).length;
    
    setNotificationCount(isModalOpen ? 0 : unviewedCount);
  }, [ordersContext, localViewedOrders, isToday, isModalOpen]);
  
  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isModalOpen && !target.closest(`.${styles.modalContent}`) && !target.closest(`.${styles.notificationIcon}`)) {
        setIsModalOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isModalOpen]);

  const toggleModal = () => {
    // Si estamos abriendo el modal, marcar todas las órdenes como vistas localmente
    if (!isModalOpen) {
      const newViewedOrders = [...localViewedOrders];
      
      // Marcar todas las órdenes externas del día como vistas
      externalOrders.forEach(order => {
        if (!order.isView && !newViewedOrders.includes(order.id)) {
          // Marcar como vista en el sistema
          onOrderViewed(order.id);
          
          // Agregar al estado local de órdenes vistas
          newViewedOrders.push(order.id);
        }
      });
      
      // Actualizar el estado local y persistirlo
      setLocalViewedOrders(newViewedOrders);
      store.setItem(VIEWED_ORDERS_KEY, newViewedOrders).catch(error => {
        console.error('Error al guardar órdenes vistas:', error);
      });
      
      // Establecer el contador de notificaciones a 0
      setNotificationCount(0);
    }
    
    setIsModalOpen(!isModalOpen);
  };

  const toggleOrderExpand = (orderId: string) => {
    // Check if the order is already expanded
    const isCurrentlyExpanded = expandedOrders.includes(orderId);

    // Mark as viewed when expanding for the first time
    if (!isCurrentlyExpanded) {
      // Marcar como vista en el sistema
      onOrderViewed(orderId);
      
      // Agregar al estado local de órdenes vistas si no está ya
      if (!localViewedOrders.includes(orderId)) {
        const newViewedOrders = [...localViewedOrders, orderId];
        setLocalViewedOrders(newViewedOrders);
        
        // Persistir el estado
        store.setItem(VIEWED_ORDERS_KEY, newViewedOrders).catch(error => {
          console.error('Error al guardar órdenes vistas:', error);
        });
      }
    }

    setExpandedOrders(prev => {
      if (isCurrentlyExpanded) {
        return prev.filter(id => id !== orderId);
      } else {
        return [...prev, orderId];
      }
    });
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  return (
    <>
      <div className={styles.notificationIcon} onClick={toggleModal}>
        <NotificationsTwoTone />
        {notificationCount > 0 && (
          <div className={styles.badge}>{notificationCount}</div>
        )}
      </div>

      {isModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <div className={styles.modalTitle}>
                {t('terminal.notifications.title', 'Notificaciones')}
              </div>
              <button className={styles.closeButton} onClick={() => setIsModalOpen(false)}>
                &times;
              </button>
            </div>
            
            {/* Buscador de notificaciones */}
            <div className={styles.searchContainer}>
              <div className={styles.searchInputContainer}>
                <SearchTwoTone className={styles.searchIcon} />
                <input
                  type="text"
                  className={styles.searchInput}
                  placeholder={t('terminal.notifications.searchPlaceholder', 'Buscar por nombre...')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button 
                    className={styles.clearSearchButton}
                    onClick={() => setSearchTerm('')}
                    aria-label={t('terminal.notifications.clearSearch', 'Limpiar búsqueda')}
                  >
                    &times;
                  </button>
                )}
              </div>
            </div>

            {externalOrders.length > 0 ? (
              <ul className={styles.ordersList}>
                {externalOrders
                  .filter(order => {
                    // Filtrar por término de búsqueda si existe
                    if (!searchTerm) return true;
                    
                    // Buscar en el nombre de la orden externa o en el número de orden
                    const searchTermLower = searchTerm.toLowerCase();
                    
                    // Buscar específicamente en el número de orden (orderName)
                    if (order.orderName.toString().includes(searchTerm)) {
                      return true;
                    }
                    
                    // Buscar en el nombre externo o en el texto completo
                    const displayName = order.external?.name || t('terminal.notifications.externalOrder', 'Orden Externa');
                    return displayName ? displayName.toLowerCase().includes(searchTermLower) : false;
                  })
                  // Ordenar por fecha de forma descendente (más recientes primero)
                  .sort((a, b) => b.dateStart - a.dateStart)
                  .map(order => (
                  <li 
                    key={order.id} 
                    className={styles.orderItem}
                    onClick={(e) => {
                      // Solo seleccionar la orden si se hace clic fuera del botón de expandir
                      const target = e.target as HTMLElement;
                      if (onSelectOrder && !target.closest(`.${styles.expandButton}`)) {
                        onSelectOrder(order.id);
                        setIsModalOpen(false); // Cerrar el modal después de seleccionar la orden
                      }
                    }}
                  >
                    <div className={styles.orderItemContainer}>
                      {/* Contenido principal de la orden */}
                      <div className={styles.orderContent}>
                        {!expandedOrders.includes(order.id) ? (
                          // Vista colapsada (una sola fila)
                          <div className={styles.orderItemCollapsed}>
                            <div className={styles.orderMainInfo}>
                              <div className={styles.orderTitleAndTime}>
                                <div className={styles.orderName}>
                                  #{order.orderName} - {order.external?.name || t('terminal.notifications.externalOrder', 'Orden Externa')}
                                </div>
                                <div className={styles.orderTime}>
                                  {formatDate(order.dateStart)}
                                </div>
                              </div>
                              <div>
                                <span>{order.items.length} {t('common.items', 'artículos')}</span>
                                <span className={styles.itemPrice}> | ${order.totalAmount.toFixed(2)}</span>
                              </div>
                            </div>
                          </div>
                        ) : (
                          // Vista expandida (detalles completos)
                          <>
                            <div className={styles.orderHeader}>
                              <div className={styles.orderName}>
                                #{order.orderName} - {order.external?.name || t('terminal.notifications.externalOrder', 'Orden Externa')}
                              </div>
                              <div className={styles.orderTime}>
                                {formatDate(order.dateStart)}
                              </div>
                            </div>
                            <div className={styles.orderSummary}>
                              {order.external?.address && (
                                <div>{t('terminal.notifications.address', 'Dirección')}: {order.external.address}</div>
                              )}
                              {order.external?.phone && (
                                <div>{t('terminal.notifications.phone', 'Teléfono')}: {order.external.phone}</div>
                              )}
                            </div>
                          </>
                        )}
                      </div>

                      {/* Botón de expandir (siempre en la misma posición) */}
                      <button
                        className={styles.expandButton}
                        onClick={() => toggleOrderExpand(order.id)}
                        aria-label={expandedOrders.includes(order.id)
                          ? t('terminal.notifications.hideDetails', 'Ocultar detalles')
                          : t('terminal.notifications.showDetails', 'Mostrar detalles')}
                      >
                        <KeyboardArrowDownTwoTone className={expandedOrders.includes(order.id) ? styles.rotated : ''} />
                      </button>
                    </div>

                    {expandedOrders.includes(order.id) && (
                      <div className={styles.orderDetails}>
                        <div className={styles.orderItemsHeader}>
                          <span>{t('common.qty', 'Cant.')}</span>
                          <span>{t('common.item', 'Artículo')}</span>
                          <span>{t('common.total', 'Total')}</span>
                        </div>
                        {order.items.map((item, index) => (
                          <div key={index} className={styles.orderItemDetail}>
                            <span className={styles.itemQuantity}>{item.quantity}</span>
                            <span className={styles.itemName}>{getItemName(item.id)}</span>
                            <span className={styles.itemPrice}>${item.amount.toFixed(2)}</span>
                          </div>
                        ))}
                        {order.notes && (
                          <div className={styles.orderNotes}>
                            <strong>{t('terminal.notifications.notes', 'Notas')}:</strong> {order.notes}
                          </div>
                        )}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            ) : searchTerm ? (
              <div className={styles.noOrders}>
                {t('terminal.notifications.noSearchResults', 'No se encontraron resultados para tu búsqueda')}
              </div>
            ) : (
              <div className={styles.noOrders}>
                {t('terminal.notifications.noOrders', 'No hay notificaciones nuevas')}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Notifications;
